import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, ArrowUp, ExternalLink, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { mediaAPI } from '../lib/api';
import { convertGoogleDriveUrl } from '../lib/imageUtils';
import { prepareArticleContent } from '../lib/contentUtils';
import { CommentSection } from '../components/common/CommentSection';

const PLACEHOLDER_IMAGE = "https://images.unsplash.com/photo-1705291490318-6cc8cb88a1f3?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function MediaDetail() {
  const { id } = useParams();
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const response = await mediaAPI.getOne(id);
        setMedia(response.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Media coverage not found' : 'Failed to load media coverage');
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, [id]);

  // Handle smooth scrolling for citation links
  useEffect(() => {
    const handleCitationClick = (e) => {
      const target = e.target;
      if (target.classList.contains('citation-marker') || target.classList.contains('back-to-cite')) {
        e.preventDefault();
        const href = target.getAttribute('href');
        const targetElement = document.querySelector(href);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          targetElement.classList.add('citation-highlight');
          setTimeout(() => {
            targetElement.classList.remove('citation-highlight');
          }, 2000);
        }
      }
    };

    document.addEventListener('click', handleCitationClick);
    return () => document.removeEventListener('click', handleCitationClick);
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !media) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4" data-testid="media-error">
        <h1 className="font-heading text-2xl text-foreground mb-4">{error || 'Media coverage not found'}</h1>
        <Link to="/media">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Media Coverage
          </Button>
        </Link>
      </div>
    );
  }

  const citations = media.citations || [];
  const processedContent = prepareArticleContent(media.content, citations);
  const hasContent = media.content && media.content.trim() !== '';

  return (
    <div className="paper-texture min-h-screen" data-testid="media-detail-page">
      {/* Hero Image */}
      <div className="relative h-[40vh] min-h-[300px]">
        <img 
          src={convertGoogleDriveUrl(media.image) || PLACEHOLDER_IMAGE}
          alt={media.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="article-container">
            <Link to="/media" className="inline-flex items-center text-primary-foreground/80 hover:text-primary-foreground mb-4 transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Media Coverage
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="article-container py-12 md:py-16">
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="article-section"
        >
          {/* Source Badge */}
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <span className="tag-badge">{media.source}</span>
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              {formatDate(media.date)}
            </span>
          </div>

          <h1 className="article-title text-3xl md:text-4xl lg:text-5xl leading-tight mb-6">
            {media.title}
          </h1>

          {/* External Link */}
          {media.url && (
            <a 
              href={media.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              View Original Article
            </a>
          )}
        </motion.header>

        {/* Gold Divider */}
        <div className="gold-line mb-8"></div>

        {/* Description */}
        {media.description && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="article-section"
          >
            <p className="text-xl text-foreground leading-relaxed font-accent italic">
              {media.description}
            </p>
          </motion.div>
        )}

        {/* Full Content */}
        {hasContent && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="article-section-lg"
          >
            {/* Main Content */}
            <div 
              className="article-content"
              dangerouslySetInnerHTML={{ __html: processedContent }}
            />

            {/* References Section */}
            {citations.length > 0 && (
              <section className="references-section mt-12 pt-8 border-t border-border" id="references">
                <div className="flex items-center gap-2 mb-6">
                  <BookOpen className="h-5 w-5 text-accent" />
                  <h3 className="font-heading text-xl font-semibold text-foreground">
                    Sources & References
                  </h3>
                </div>
                <ol className="references-list space-y-3">
                  {citations.map((citation, idx) => (
                    <li 
                      key={idx} 
                      id={`ref-${idx + 1}`}
                      className="reference-item flex items-start gap-3 p-3 rounded-md transition-colors"
                    >
                      <span className="reference-number shrink-0 w-6 h-6 bg-accent/10 text-accent rounded text-xs font-semibold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <div className="reference-content flex-1 min-w-0">
                        <span 
                          className="citation-text"
                          dangerouslySetInnerHTML={{ __html: citation }} 
                        />
                      </div>
                      <a 
                        href={`#cite-${idx + 1}`}
                        className="back-to-cite shrink-0 text-accent hover:text-accent/80 transition-colors"
                        title="Back to text"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </a>
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </motion.div>
        )}

        {/* No content message */}
        {!hasContent && !media.description && (
          <div className="text-center py-8 text-muted-foreground">
            <p>No additional content available.</p>
            {media.url && (
              <a 
                href={media.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors mt-4"
              >
                <ExternalLink className="h-4 w-4" />
                Read the full article on {media.source}
              </a>
            )}
          </div>
        )}
      </article>

      {/* Comments Section */}
      <div className="article-container py-8">
        <CommentSection 
          contentType="media" 
          contentId={id}
        />
      </div>

      {/* Back to Media */}
      <section className="py-12 bg-muted/30 border-t border-border">
        <div className="article-container text-center">
          <Link to="/media">
            <Button variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Browse More Media Coverage
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
